import type { GHADiscountSummaryLine, GHAExclusionRuleLine } from '~/services/ConnectionGoogleService';

import { TIER_LABELS, TIER_ORDER } from './constants';

const tierLabel = (tier: string): string => TIER_LABELS[tier] ?? tier;

const tierSortIndex = (tier: string): number => {
  const index = TIER_ORDER.indexOf(tier);
  return index === -1 ? TIER_ORDER.length : index;
};

/** "3% Margin Retention" / "6% Percentage" — full words per GHA-442 review. */
export function formatDiscountKind(line: GHADiscountSummaryLine): string {
  const kind = line.discountType === 'margin_retention' ? 'Margin Retention' : 'Percentage';
  return `${line.discountValue}% ${kind}`;
}

/** Universal rules (country null) apply to every enabled country. */
export function formatCountry(country: string | null): string {
  return country ?? 'All countries';
}

/** "Expedia: do not discount Tier 1, Tier 2, Tier 3" with optional country/chain narrowing. */
export function formatExclusionRule(line: GHAExclusionRuleLine): string {
  const who = line.supplierName ?? line.supplier ?? 'All suppliers';

  const scopes: string[] = [];
  if (line.cugTiers != null && line.cugTiers.length > 0) {
    scopes.push(line.cugTiers.map(tierLabel).join(', '));
  } else {
    scopes.push('all tiers');
  }
  if (line.countries != null && line.countries.length > 0) {
    scopes.push(`in ${line.countries.join(', ')}`);
  }
  if (line.chainNames != null && line.chainNames.length > 0) {
    const chains = line.chainNames.map((chain) => chain ?? 'un-chained properties');
    scopes.push(`chains: ${chains.join(', ')}`);
  }

  return `${who}: do not discount ${scopes.join(' — ')}`;
}

/** "min ABW 0 · min LOS 1" per distinct coordinate; the estate currently has exactly one. */
export function formatRuleCoordinates(
  coordinates: Array<{ minAdvanceBookingWindow: number; minLengthOfStay: number }>,
): string {
  return coordinates
    .map((coordinate) => `min ABW ${coordinate.minAdvanceBookingWindow} · min LOS ${coordinate.minLengthOfStay}`)
    .join('; ');
}

/** Ordered [tier, label, count] entries for chip rendering, canonical tier order, zeros skipped. */
export function tierEntries(byTier: Record<string, number>): Array<[string, string, number]> {
  return Object.entries(byTier)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => tierSortIndex(a) - tierSortIndex(b) || a.localeCompare(b))
    .map(([tier, count]) => [tier, tierLabel(tier), count]);
}

/** Compact chip label: "Expedia — Tier 1, Tier 2, Tier 3" (full detail stays in the tooltip). */
export function formatExclusionRuleShort(line: GHAExclusionRuleLine): string {
  const who = line.supplierName ?? line.supplier ?? 'All suppliers';
  const tiers =
    line.cugTiers != null && line.cugTiers.length > 0 ? line.cugTiers.map(tierLabel).join(', ') : 'all tiers';
  return `${who} — ${tiers}`;
}

/** Share of live properties covered by a discount line, clamped 0–100; null when unknowable. */
export function coveragePercent(propertyCount: number, liveTotal: number): number | null {
  if (liveTotal <= 0) {
    return null;
  }
  return Math.min(100, Math.round((propertyCount / liveTotal) * 100));
}
